import { useEffect, useState } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, type ApiError } from '@/lib/api';

interface TenantSettings {
  name: string;
  slug: string;
  logoUrl: string | null;
  primaryColor: string;
  customDomain: string | null;
  planTier: string;
}

export function Settings() {
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [form, setForm] = useState({ name: '', primaryColor: '#2563EB', logoUrl: '', customDomain: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    api.get<TenantSettings>('/tenants/settings')
      .then((r) => {
        setSettings(r.data);
        setForm({
          name: r.data.name,
          primaryColor: r.data.primaryColor,
          logoUrl: r.data.logoUrl ?? '',
          customDomain: r.data.customDomain ?? '',
        });
      })
      .catch(() => setMessage({ type: 'error', text: 'Failed to load settings.' }))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const r = await api.put<TenantSettings>('/tenants/settings', {
        name: form.name,
        primaryColor: form.primaryColor,
        logoUrl: form.logoUrl || null,
        customDomain: form.customDomain || null,
      });
      setSettings(r.data);
      setMessage({ type: 'success', text: 'Settings saved successfully.' });
    } catch (e) {
      const err = e as ApiError;
      setMessage({ type: 'error', text: err.message ?? 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <section className="mx-auto max-w-2xl px-6 py-10 space-y-6">
        {/* Agency Info */}
        <Card>
          <CardHeader>
            <CardTitle>Agency Profile</CardTitle>
            <CardDescription>Your agency name and slug (cannot change slug).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Agency Name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="slug">Slug</Label>
              <Input id="slug" value={settings?.slug ?? ''} disabled className="bg-muted" />
              <p className="text-xs text-muted-foreground">Slug cannot be changed after registration.</p>
            </div>
          </CardContent>
        </Card>

        {/* Branding */}
        <Card>
          <CardHeader>
            <CardTitle>Branding</CardTitle>
            <CardDescription>Customize the look and feel of your agency portal.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="color">Primary Color</Label>
              <div className="flex gap-2 items-center">
                <Input
                  id="color"
                  value={form.primaryColor}
                  onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
                  className="w-32"
                  placeholder="#2563EB"
                />
                <div
                  className="size-8 rounded border shrink-0"
                  style={{ backgroundColor: form.primaryColor || '#2563EB' }}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="logo">Logo URL</Label>
              <Input
                id="logo"
                value={form.logoUrl}
                onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
                placeholder="https://example.com/logo.png"
              />
            </div>
          </CardContent>
        </Card>

        {/* Domain */}
        <Card>
          <CardHeader>
            <CardTitle>Custom Domain</CardTitle>
            <CardDescription>Serve your portal from your own domain.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              <Label htmlFor="domain">Domain</Label>
              <Input
                id="domain"
                value={form.customDomain}
                onChange={(e) => setForm({ ...form, customDomain: e.target.value })}
                placeholder="portal.myagency.com"
              />
            </div>
          </CardContent>
        </Card>

        {/* Message */}
        {message && (
          <div
            className={`text-sm p-3 rounded-lg border ${
              message.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-700 dark:bg-green-950 dark:border-green-800 dark:text-green-400'
                : 'bg-destructive/10 border-destructive/20 text-destructive'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Save */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Save className="size-4 mr-2" />}
            Save Settings
          </Button>
        </div>
    </section>
  );
}
